import { ChangeEvent, useCallback, useState } from "react"

import ConfirmationModal, {
  ModalInfo,
} from "components/Modals/ConfirmationModal"
import { Container, Header } from "components/Page"
import {
  activatePackage,
  createPackageItem,
  deactivatePackage,
  deletePackage,
  fetchPackage,
  fetchPackageItems,
  patchPackage,
} from "api/packages"
import { css, cx } from "@emotion/css"
import { defaultSort, limitAndOffset } from "api/utils"
import {
  fetchMediaTypeSemantics,
  fetchSubmediaTypeSemantics,
} from "api/globalMediaTypes"
import { queryCache, useMutation, useQuery } from "react-query"
import { useHistory, useParams } from "react-router"

import { ApiError } from "api/http/utils"
import Badge from "components/Badge"
import Breadcrumbs from "components/Breadcrumbs"
import Button from "ui/buttons/Button"
import { ButtonGroup } from "components/buttons/ButtonGroup"
import { ReactComponent as CheckIcon } from "assets/check.svg"
import { ColorMap } from "utils/color"
import { DateInput } from "components/inputs/DatePicker"
import { DateRangeAgain } from "components/inputs/DateRange"
import { ReactComponent as DeactivateIcon } from "assets/icon-deactivate.svg"
import { ReactComponent as DeleteIcon } from "assets/delete.svg"
import MoreActions from "components/MoreActions"
import Options from "components/inputs/Options"
import { Package } from "models/packages"
import { PackageItem } from "models/packages"
import SubMedia from "./SubMedia"
import Surface from "components/Surface"
import { TextInput } from "components/inputs/TextInput"
import { sortByDefault } from "routes/Base/Admin/MarketAgencies/Details/Companies/columns"
import { useAuthenticated } from "providers/A12n"
import { useFeedback } from "components/FeedbackSnackbar"
import { useI18n } from "providers/I18n"
import { useLocationSearch } from "hooks/useLocationSearch"
import { zonedTimeToUtc } from "date-fns-tz"

type PackageValue = "cost" | "value" | "spotsDuration"
const InitialState = {
  cost: undefined,
  value: undefined,
  spotsDuration: undefined,
}
export type IErrors = {
  tableErrors?: ITableErrors[]
  detailsErrors?: IDetailsErrors[]
}

type IDetailsErrors = {
  field?: string
  message?: unknown
}

type ITableErrors = {
  rowId?: string
  name?: string
  message?: unknown
}

function PackageDetails() {
  const { t } = useI18n()
  const { id } = useParams<{ id: string }>()
  const { hasAuthority, meta } = useAuthenticated()
  const { timeZone = "Etc/UTC" } = meta ?? {}
  const [open, setOpen] = useState(false)
  const [newSubMedia, setNewSubMedia] = useState<string>()
  const [error, setError] = useState<string[] | undefined>()
  const ls = useLocationSearch()
  const sendFeedback = useFeedback((s) => s.send)
  const history = useHistory()
  const params = ls
    .map(limitAndOffset)
    .map(defaultSort(sortByDefault))
    .toString()
  const [modalInfo, setModalInfo] = useState<ModalInfo>()
  const isModalOpen = Boolean(modalInfo)
  const [activationErrors, setActivationErrors] = useState<IErrors>()
  const [openSubmedia, setOpenSubmedia] = useState<string[]>([])

  const createAllowed = hasAuthority(["AUTH_CREATE_RATE_CARD"])
  const editAllowed = hasAuthority(["AUTH_EDIT_RATE_CARD"])

  const { data: semantics } = useQuery(
    ["media-types-semantics"],
    fetchMediaTypeSemantics,
  )
  const { data: items } = useQuery(
    ["package-items", id, params],
    fetchPackageItems,
  )
  const { data } = useQuery(["package-details", id], fetchPackage, {
    onSuccess: (d) => {
      setValue(d)
    },
  })
  const [value, setValue] = useState<Partial<Package | undefined>>(
    () => InitialState,
  )

  const [activate] = useMutation(activatePackage, {
    onSuccess: () => {
      setModalInfo(undefined)
      sendFeedback(t("package.activate.success"), "success")
      queryCache.invalidateQueries(["package-details"])
    },
    onError: (error: any) => {
      setModalInfo(undefined)
      if (error.fields) {
        if (!error.fields.supplierPackageItems) {
          const details = Object.entries(error.fields).map((key) => ({
            field: key[0].toString(),
            message: key[1],
          }))
          setActivationErrors({ detailsErrors: details })
        } else {
          const rowIds = Object.keys(error.fields.supplierPackageItems)
          const errors = rowIds.flatMap((rowId: string) => {
            return Object.entries(error.fields.supplierPackageItems[rowId]).map(
              (key) => ({
                rowId: rowId,
                name: key[0].toString(),
                message: key[1],
              }),
            )
          })
          setActivationErrors({ tableErrors: errors })
        }
      }

      sendFeedback(t(error.message) || t("package.activate.error"), "error")
      queryCache.invalidateQueries(["package-details"])
    },
  })

  const [deactivate] = useMutation(deactivatePackage, {
    onSuccess: () => {
      setModalInfo(undefined)
      sendFeedback(t("package.deactivate.success"), "success")
      queryCache.invalidateQueries(["package-details"])
    },
    onError: (e) => {
      setModalInfo(undefined)
      sendFeedback(t(e.message) || t("package.deactivate.error"), "error")
      queryCache.invalidateQueries(["package-details"])
    },
  })

  const handleActivate = useCallback(() => {
    setModalInfo({
      type: "primary",
      title: t("package.modal.activate.title"),
      description: t("package.modal.activate.description"),
      submitLabel: t("actions.activate"),
      onSubmit: async () => await activate(id || ""),
    })
  }, [activate, t, id])

  const handleDeactivate = useCallback(() => {
    setModalInfo({
      type: "primary_destructive",
      title: t("package.modal.deactivate.title"),
      description: t("package.modal.deactivate.description"),
      submitLabel: t("actions.deactivate"),
      onSubmit: async () => await deactivate(id || ""),
    })
  }, [deactivate, t, id])

  const semanticsMapped = semantics?.length
    ? semantics.map((s) => ({ value: s.code, label: s.name }))
    : []

  const { data: submediaTypesSemantics } = useQuery(
    ["submedia-types-semantics", data?.mediaTypeSemanticCode],
    fetchSubmediaTypeSemantics,
    {
      enabled: !!data?.mediaTypeSemanticCode,
    },
  )

  const subMediaSemanticsMapped =
    submediaTypesSemantics?.map((s) => ({
      value: s.code,
      label: s.name,
    })) || []

  const [create] = useMutation(createPackageItem, {
    onSuccess: () => {
      queryCache.invalidateQueries(["package-items", id, params])
      setOpen(false)
      setError(undefined)
    },
    onError: (error: ApiError) => {
      /*   queryCache.invalidateQueries("region")
      setError(error.fields?.map((e: any) => e.key)) */
      setError(error.fields?.map((e: any) => e.key))
    },
  })

  const [_delete] = useMutation(deletePackage, {
    onSuccess: () => {
      setModalInfo(undefined)
      sendFeedback(t("package.delete.success"), "success")
      history.push(`/market-vendors/${data?.supplierId}/rate-cards/packages`)
    },
    onError: (e) => {
      setModalInfo(undefined)
      sendFeedback(t(e.message) || t("package.delete.error"), "error")
      queryCache.invalidateQueries(["package-details"])
    },
  })

  const handleDelete = useCallback(() => {
    setModalInfo({
      type: "primary_destructive",
      title: t("package.modal.delete.title"),
      description: t("package.modal.delete.description"),
      submitLabel: t("actions.delete"),
      onSubmit: async () => await _delete(id || ""),
    })
  }, [_delete, t, id])

  const canActivateOrDelete = data?.status !== "ACTIVE"
  const [update] = useMutation(patchPackage, {
    onSuccess: () => {
      queryCache.invalidateQueries(["package-details", id])
    },
    onError: (err: ApiError) => {
      // const error = err as ApiError
      // const errorMap = error?.errorsMap ?? {}
      // setErrors(errorMap)
    },
  })

  const hasSubmediaTypes = items && items.length > 0
  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    update({ [e.target.name]: e.target.value, id })
  }

  const handleDateChange = (date: Date | null) => {
    update({ expirationDate: date?.toISOString(), id })
  }

  const onDateRangeChange = (s: Date | null, e: Date | null) => {
    if (s && e) {
      update({
        blackoutPeriodStart: zonedTimeToUtc(s, timeZone).toISOString(),
        blackoutPeriodEnd: zonedTimeToUtc(e, timeZone).toISOString(),
        id,
      })
    }
  }

  const onDateRangeClear = () => {
    update({
      blackoutPeriodStart: "",
      blackoutPeriodEnd: "",
      id,
    })
  }

  const handleBlur = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    !!value &&
      update({ [e.target.name]: value[e.target.name as PackageValue], id })
  }

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setValue({
      ...value,
      [e.target.name]: parseInt(e.currentTarget.value),
    })
    const errors = activationErrors?.detailsErrors?.filter(
      (err) => err?.field !== e?.target.name,
    )
    setActivationErrors({ detailsErrors: errors })
  }

  const clearTableErrors = (rowId: string, name: string) => {
    const errors =
      activationErrors?.tableErrors?.filter(
        (e) => !(e?.rowId === rowId && e?.name === name),
      ) || []
    setActivationErrors({ tableErrors: errors })
  }

  const handleOpenSubmedias = (ids: string[]) => {
    setOpenSubmedia(ids)
  }

  const isActive = data?.status === "ACTIVE"

  return (
    <Container>
      <Breadcrumbs
        current={"PackageDetails"}
        items={[
          {
            backLink: `${t("nav.admin")} / ${t("Package")}`,
            to: `/market-vendors/${data?.supplierId}/rate-cards/packages`,
          },
        ]}
      />
      <Header
        label={data?.name ?? ""}
        badge={
          <Badge color={ColorMap[data?.status || ""]}>{data?.status}</Badge>
        }
      >
        <ButtonGroup>
          <MoreActions
            options={
              canActivateOrDelete
                ? [
                    // ** ACTIVATE
                    {
                      label: t("rate.card.activate.action"),
                      icon: <CheckIcon />,
                      onClick: handleActivate,
                    },
                    // ** DELETE
                    {
                      label: t("rate.card.delete.action"),
                      icon: <DeleteIcon />,
                      onClick: handleDelete,
                    },
                  ]
                : [
                    // ** DEACTIVATE
                    {
                      label: t("rate.card.deactivate.action"),
                      icon: <DeactivateIcon />,
                      onClick: handleDeactivate,
                    },
                  ]
            }
          />
        </ButtonGroup>
      </Header>
      <Surface>
        <div>
          <section className={styles.section}>
            <h2>{t("vendors.profile.section.details")}</h2>
            <div className="fields">
              <div className={styles.fieldRow}>
                <Options
                  onChange={handleChange}
                  name="mediaTypeSemanticCode"
                  label={t("packages.media.type")}
                  options={semanticsMapped}
                  value={data?.mediaTypeSemanticCode}
                  size="medium"
                  style={{ marginTop: 16 }}
                  className={styles.fieldWidth}
                  disabled
                  fullWidth
                />
                <TextInput
                  type="number"
                  name="spotsDuration"
                  label={t("package.spotsDuration")}
                  margin="normal"
                  size="medium"
                  className={styles.fieldWidth}
                  value={value?.spotsDuration ?? ""}
                  onChange={handleInputChange}
                  disabled={isActive || !editAllowed}
                  onBlur={handleBlur}
                  error={
                    activationErrors &&
                    Boolean(
                      activationErrors?.detailsErrors?.some(
                        (e: IDetailsErrors) => e.field === "spotsDuration",
                      ),
                    )
                  }
                />
                <TextInput
                  name="cost"
                  label={t("package.cost")}
                  margin="normal"
                  size="medium"
                  type="number"
                  className={styles.fieldWidth}
                  value={value?.cost ?? ""}
                  onChange={handleInputChange}
                  disabled={isActive || !editAllowed}
                  onBlur={handleBlur}
                  error={
                    activationErrors &&
                    Boolean(
                      activationErrors?.detailsErrors?.some(
                        (e: IDetailsErrors) => e.field === "cost",
                      ),
                    )
                  }
                />
              </div>
              <div className={styles.fieldRow}>
                <DateInput
                  name="expirationDate"
                  label={t("packages.expiration.date")}
                  margin="normal"
                  size="medium"
                  value={data?.expirationDate}
                  onChange={handleDateChange}
                  inputVariant="outlined"
                  className={styles.fieldWidth}
                  disabled={isActive || !editAllowed}
                  hasStartAdornment
                />
                <DateRangeAgain
                  start={value?.blackoutPeriodStart}
                  end={value?.blackoutPeriodEnd}
                  label={t("package.blackout.period")}
                  className={styles.date}
                  hasStartAdornment
                  onRangeChange={onDateRangeChange}
                  onRangeClear={onDateRangeClear}
                  disabled={isActive || !editAllowed}
                />
                <TextInput
                  name="value"
                  label={t("package.value")}
                  margin="normal"
                  size="medium"
                  type="number"
                  className={styles.fieldWidth}
                  value={value?.value ?? ""}
                  onChange={handleInputChange}
                  disabled={isActive || !editAllowed}
                  onBlur={handleBlur}
                />
              </div>
            </div>
          </section>
          <div className={styles.subMediaSection}>
            <div>
              <strong> {"Submedia Types"}</strong>
              {hasSubmediaTypes && !isActive && createAllowed && (
                <strong
                  className={styles.createNew}
                  onClick={() => setOpen(true)}
                >
                  {"+ New Submedia Type"}
                </strong>
              )}
            </div>
          </div>
          {open && (
            <>
              <div
                className={cx(styles.createSubRg, {
                  [styles.createSubRgOpen]: open,
                })}
              >
                <Options
                  onChange={(e) => setNewSubMedia(e.target.value.toString())}
                  placeholder={"Select Submedia Type..."}
                  name="mediaTypeSemantic"
                  label={t("packages.submedia.type")}
                  options={subMediaSemanticsMapped}
                  value={newSubMedia}
                  size="small"
                  className={styles.subMediaOptions}
                />
                <div>
                  <Button
                    className={styles.createButton}
                    variant="outlined"
                    onClick={() => {
                      setOpen(false)
                      setNewSubMedia("")
                      setError(undefined)
                    }}
                    color="primary"
                  >
                    {t("actions.cancel")}
                  </Button>
                  <Button
                    className={styles.createButton}
                    onClick={() =>
                      newSubMedia &&
                      create({
                        mediaSubtypeSemanticCode: newSubMedia,
                        id: id,
                        type: "media_subtype_plan",
                      })
                    }
                    variant="contained"
                    color="primary"
                    disabled={newSubMedia === "" || newSubMedia === undefined}
                  >
                    {t("actions.create")}
                  </Button>
                </div>
              </div>
              <div className={styles.errorMessage}>
                {error ? error.map((error: any) => t(error)) : ""}
              </div>
            </>
          )}
        </div>
        <>
          {!open && !hasSubmediaTypes && !isActive && createAllowed && (
            <>
              <div className={styles.emptyMessage}>
                {t("packages.submedias.empty.list")}
              </div>

              <div
                onClick={() => setOpen(true)}
                className={styles.emptySubMedia}
              >
                {t("packages.submedias.empty.list.add")}
              </div>
            </>
          )}
          {hasSubmediaTypes && (
            <div className={cx("BodyBody-2Dark-greyRegular", styles.header)}>
              <span>Submedia Type </span>
              <div>
                <span
                  className={cx(styles.headerItem, {
                    [styles.financeHeaderItem]: true,
                  })}
                >
                  Line Items
                </span>
                <span>Actions</span>
              </div>
            </div>
          )}
          {items &&
            data &&
            items.map(
              (item: PackageItem, idx: number) =>
                item.type === "media_subtype_plan" && (
                  <div className={styles.subMedias} key={idx}>
                    <SubMedia
                      open={openSubmedia}
                      handleOpenSubmedias={handleOpenSubmedias}
                      activateErrors={activationErrors}
                      item={item}
                      supplierId={data.supplierId}
                      packageId={id}
                      submediaTypesSemantics={subMediaSemanticsMapped}
                      isActive={isActive}
                      mediaTypeSemantic={data?.mediaTypeSemanticCode}
                      clearErrors={clearTableErrors}
                    />
                  </div>
                ),
            )}
        </>
      </Surface>
      <ConfirmationModal
        open={isModalOpen}
        onCancel={() => setModalInfo(undefined)}
        {...modalInfo}
      />
    </Container>
  )
}
export default PackageDetails
const styles = {
  subMedias: css`
    margin-bottom: var(--spacing-xs);
  `,
  section: css`
    display: flex;
    flex-direction: column;
    padding-bottom: var(--spacing-l);
    padding-top: var(--spacing-m);

    > h2,
    .title {
      font-size: 0.875rem;
      font-weight: bold;
      color: var(--black, #000);
      margin-bottom: calc(var(--spacing-xl, 24px) - 16px);
    }

    .fields {
      display: flex;
      flex-direction: column;
    }
  `,
  sectionTitle: css`
    font-size: 14px;
    font-weight: bold;
    text-transform: capitalize;
  `,

  fieldRow: css`
    display: flex;
    gap: var(--spacing-m, 16px);
  `,
  fieldWidth: css`
    width: 233px;
  `,
  date: css`
    margin-top: 16px;
    .MuiOutlinedInput-marginDense {
      height: 48px;
      width: 233px;
    }
    .MuiInputLabel-outlined.MuiInputLabel-marginDense {
      transform: translate(38px, 17px) scale(1);
    }
  `,
  subMediaSection: css`
    margin-top: 43px;
    font-size: 14px;
    color: var(--black);
    margin-bottom: 45px;

    > * {
      margin-bottom: 13px;
    }
  `,
  createNew: css`
    font-size: 13px;
    color: var(--primary-main);
    margin-left: 22px;
    cursor: pointer;
  `,
  createButton: css`
    margin-left: var(--spacing-xs);
    padding: 5px 10px 5px 10px;
    width: 80px;
  `,
  inputClass: css`
    outline: none;
    border: none;
    width: 989px;
  `,
  createSubRg: css`
    align-items: center;
    border: dashed 1px var(--primary-main);
    height: 46px;
    padding: 11px 39px 11px 14px;
    display: flex;
    justify-content: space-between;
    font-size: 14px;
    color: var(--dark-grey);
    width: 989px;
    display: none;
  `,
  createSubRgOpen: css`
    display: flex;
  `,
  errorMessage: css`
    font-size: 13px;
    color: var(--light-red);
  `,
  subMediaOptions: css`
    width: 233px;
  `,
  emptyMessage: css`
    margin: 27px 0px 8px 0px;
    font-size: 16px;
    font-weight: 300;
    text-align: center;
    color: var(--dark-grey);
  `,
  emptySubMedia: css`
    font-size: 13px;
    font-weight: bold;
    text-align: center;
    color: var(--primary-main);
    cursor: pointer;
  `,
  header: css`
    display: flex;
    justify-content: space-between;
    padding: 11px 24px 0px 24px;
    width: 989px;
    margin-bottom: var(--spacing-xs);
  `,
  financeHeaderItem: css`
    margin-right: 143px;
  `,
  headerItem: css`
    margin-right: 100px;
  `,
}
